import { type DateRange, type Employee, dateAdd, getCurrentMonthRange, getRange, toDateString } from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import { type TemplateResult, html } from "lit";
import { createRoute, lazyElement } from "../../../lib/tsr/router";
import { redirectTo } from "../../app-router-context";
import { employeeRoute } from "../../app-routes";
import { type BalanceKind, employeeBalancesQuery } from "../all/balances.queries";
import type { EmployeeLayoutRoute } from "./employee.route";

type Resolution = "month" | "week" | "year";

interface BalanceSearch extends Partial<DateRange> {
    resolution?: Resolution;
}

const RESOLUTIONS: Resolution[] = ["week", "month", "year"];

function validateSearch(search: Record<string, unknown>): BalanceSearch {
    return {
        from: search.from as DateString | undefined,
        to: search.to as DateString | undefined,
        resolution: RESOLUTIONS.includes(search.resolution as Resolution) ? (search.resolution as Resolution) : "month",
    };
}

/** First contract to today (or the contract's end), snapped to the resolution. */
function defaultRange(employee: Employee | null | undefined, resolution: Resolution, kind: BalanceKind): DateRange {
    const firstContract = employee?.firstContract;
    if (!firstContract) {
        return getCurrentMonthRange();
    }
    const from = getRange(firstContract.start, resolution).from;
    const today = toDateString(new Date());
    // The time balance ends on the contract's last day, the others on the exclusive end date.
    const latest = employee?.latestContract;
    const contractEnd = kind === "time" ? latest?.inclusiveEnd : latest?.end;
    const to = getRange(!contractEnd || today < contractEnd ? today : contractEnd, resolution).to;
    return { from, to };
}

const ledgersRange = (employee: Employee): DateRange | null => {
    const latestContract = employee.latestContract;
    const firstContract = employee.firstContract;
    if (!firstContract || !latestContract) {
        return null;
    }
    const endOfCurrentMonth = getCurrentMonthRange().to;
    return {
        from: getRange(firstContract.start, "month").from,
        to:
            latestContract.end && latestContract.end < endOfCurrentMonth
                ? getRange(dateAdd(latestContract.end, { days: -1 }), "month").to
                : endOfCurrentMonth,
    };
};

interface BalancePage<K extends BalanceKind> {
    employeeId: number;
    balances: Awaited<ReturnType<ReturnType<typeof employeeBalancesQuery<K>>["queryFn"]>>;
    range: DateRange;
    resolution: Resolution;
}

export function createEmployeeBalancesRoutes(parent: EmployeeLayoutRoute) {
    const balanceRoute = <K extends BalanceKind>(
        kind: K,
        page: "time-balance" | "vacation-balance" | "bonuses-balance",
        title: string,
        render: (props: BalancePage<K>) => TemplateResult,
        load: () => Promise<unknown>
    ) =>
        createRoute({
            getParentRoute: () => parent,
            path: page,
            staticData: {
                title: ({ match, context }) => `${title}: ${context.app.getEmployee(match.params.employeeId)?.name}`,
                menu: employeeRoute(page),
            },
            validateSearch,
            beforeLoad: ({ params, search, context }): void => {
                if (!search.from || !search.to) {
                    const range = defaultRange(context.app.getEmployee(params.employeeId), search.resolution ?? "month", kind);
                    redirectTo(`/employees/${params.employeeId}/${page}`, { ...search, ...range });
                }
            },
            loaderDeps: ({ search }) => ({ from: search.from!, to: search.to!, resolution: search.resolution ?? "month" }),
            loader: ({ params, deps, context }) =>
                context.queryClient.fetchQuery({
                    ...employeeBalancesQuery(context.app, kind, { employeeId: params.employeeId, ...deps }),
                    staleTime: 30_000,
                }),
            component: ({ match }): TemplateResult =>
                render({
                    employeeId: match.params.employeeId,
                    balances: (match.loaderData ?? []) as BalancePage<K>["balances"],
                    range: { from: match.search.from!, to: match.search.to! },
                    resolution: match.search.resolution ?? "month",
                }),
        }).lazy(lazyElement(load));

    const timeBalance = balanceRoute(
        "time",
        "time-balance",
        "Arbeitszeitkonto",
        (p) =>
            html`<ptc-employees-time-balance-single
                class="fullbleed"
                .employeeId=${p.employeeId}
                .balances=${p.balances}
                .range=${p.range}
                .resolution=${p.resolution}
            ></ptc-employees-time-balance-single>`,
        () => import("./employees-time-balance-single")
    );

    const vacationBalance = balanceRoute(
        "vacation",
        "vacation-balance",
        "Urlaubskonto",
        (p) =>
            html`<ptc-employees-vacation-balance-single
                class="fullbleed"
                .employeeId=${p.employeeId}
                .balances=${p.balances}
                .range=${p.range}
                .resolution=${p.resolution}
            ></ptc-employees-vacation-balance-single>`,
        () => import("./employees-vacation-balance-single")
    );

    const bonusesBalance = balanceRoute(
        "bonuses",
        "bonuses-balance",
        "Zuschlagskonto",
        (p) =>
            html`<ptc-employees-bonuses-balance-single
                class="fullbleed"
                .employeeId=${p.employeeId}
                .balances=${p.balances}
                .range=${p.range}
                .resolution=${p.resolution}
            ></ptc-employees-bonuses-balance-single>`,
        () => import("./employees-bonuses-balance-single")
    );

    const ledgers = createRoute({
        getParentRoute: () => parent,
        path: "ledgers",
        staticData: {
            title: ({ match, context }) => `Konten: ${context.app.getEmployee(match.params.employeeId)?.name}`,
            menu: employeeRoute("ledgers"),
        },
        loader: async ({ params, context }) => {
            const employee = context.app.getEmployee(params.employeeId);
            const range = employee && ledgersRange(employee);
            if (!range) {
                return null;
            }
            const query = (kind: BalanceKind) => ({
                ...employeeBalancesQuery(context.app, kind, {
                    employeeId: params.employeeId,
                    ...range,
                    resolution: "month",
                }),
                staleTime: 30_000,
            });
            const [time, vacation, bonuses] = await Promise.all([
                context.queryClient.fetchQuery(query("time")),
                context.queryClient.fetchQuery(query("vacation")),
                context.queryClient.fetchQuery(query("bonuses")),
            ]);
            return { range, time, vacation, bonuses };
        },
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-ledgers-single
                class="fullbleed"
                .employeeId=${match.params.employeeId}
                .data=${match.loaderData}
            ></ptc-employees-ledgers-single>`,
    }).lazy(lazyElement(() => import("./employees-ledgers-single")));

    return [timeBalance, vacationBalance, bonusesBalance, ledgers] as const;
}
